import { useContext, useEffect, useState } from 'react';
import { NavLink, Location, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Drawer, List, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';

import { PopupType } from '@/types';
import { ctxUser } from '@/contexts/UserContext';
import { chatContext } from '@/contexts/ChatContext';
import { MenuHeader } from '@/components/home/MenuCloneIcons';

import './Home.css';

// olyan menupont, aminek van gyereke
// klikk-re kinyilik/becsukodik
// tobb submenu is lehet, az egyik kinyitasa becsukja a tobbit
// ha nyitott, kilistazza a gyerek menupontokat
function Submenu({
  popup,
  drawerMenuOpen,
  opened,
  setOpened,
}: {
  popup: PopupType;
  drawerMenuOpen: boolean;
  opened: string | undefined;
  setOpened: (n: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const isOpen = opened === popup.title;
  return (
    <Box
      onClick={() => {
        if (isOpen) setOpened(undefined);
        else setOpened(popup.title);
      }}
      sx={{ width: '100%' }}
    >
      <Stack
        direction="row"
        sx={{ gap: 2, alignContent: 'center' }}
        data-qa-element-id={`left-navigation-action-${popup.title}`}
      >
        <ListItemIcon sx={{ minWidth: 45, justifyContent: 'center', alignItems: 'center', height: 48 }}>
          {popup.icon}
        </ListItemIcon>
        {drawerMenuOpen && (
          <Stack direction="row" sx={{ justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <ListItemText primary={t(popup.title!)} />
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </Stack>
        )}
      </Stack>
      {isOpen && popup.children?.length && (
        <Stack data-qa-element-id={`submenu_for_${popup.title}`}>
          <IconList
            popups={popup.children}
            drawerMenuOpen={drawerMenuOpen}
            opened={opened}
            setOpened={setOpened}
            className="home-left-submenu"
          />
        </Stack>
      )}
    </Box>
  );
}

function getTo(p: PopupType, location: Location) {
  if (p.level === 1) {
    // ha a path vagy a navigateTo az aktualis akkor legyek aktiv
    if (location.pathname === p.path) return location.pathname;
    if (location.pathname === p.navigateTo) return location.pathname;
    // egyebkent tok mindegy, ugysem hasznaljuk
    return p.path;
  }
  // masodik szint eseten
  if (location.pathname.endsWith(p.path)) return location.pathname; // ha en vagyok a vegen az egesz legyen az aktualis h aktiv legyek
  return p.path; // egyebkent tok mindegy, ugysem hasznaljuk
}

function getNav(p: PopupType, location: Location) {
  if (p.level === 1) {
    // ha aktiv vagyok pont a masikra kell menni :(
    if (location.pathname === p.path) return p.navigateTo!;
    if (location.pathname === p.navigateTo) return p.path;
    // ha nem vagyok aktiv akkor az egyikre kell menni a ketto kozul
    // eldonthetjuk melyikkel induljon ilyenkor, a select vagy a select nelkulivel ?
    return p.navigateTo! || p.path;
  }

  // ha en vagyok az aktiv akkor az 1-es szintu-re menjen vissza
  if (location.pathname.endsWith(p.path))
    return location.pathname.substring(0, location.pathname.length - p.path.length - 1);
  // nem en vagyok az aktiv
  // mi van ha az elso resz nem a tracking vagy a route ?? ha a reports-rol jon ???
  const parts = location.pathname.split('/'); // regex lehet jobb lenne
  let level1path = parts[1]; // a nulladik elem ures string :(
  // ez sajnos be van egetve
  if (level1path !== 'tracking' && level1path !== 'routes') {
    level1path = 'tracking';
  }
  return '/' + level1path + '/' + p.path;
}

// olyan menupont, aminek nincs gyereke
function MenuItem({ popup, drawerMenuOpen }: { popup: PopupType; drawerMenuOpen: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <NavLink
      to={getTo(popup, location)}
      end
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await navigate(getNav(popup, location)); // sajnos a to nem jo helyre mutat :(
      }}
      data-qa-element-id={`left-navigation-action-${popup.title}`}
      className={({ isActive }) => (isActive ? 'Mui-selected' : '')}
      style={{ width: '100%', textDecoration: 'none' }}
    >
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
        <ListItemIcon sx={{ minWidth: 45, justifyContent: 'center', alignItems: 'center', height: 48 }}>
          {popup.icon}
        </ListItemIcon>

        {drawerMenuOpen && <ListItemText primary={t(popup.title!)} />}
      </Stack>
    </NavLink>
  );
}

function IconList({
  popups,
  drawerMenuOpen,
  className,
  opened,
  setOpened,
}: {
  popups: PopupType[];
  drawerMenuOpen: boolean;
  className?: string;
  opened: string | undefined;
  setOpened: (n: string | undefined) => void;
}) {
  const userCtx = useContext(ctxUser);
  const chatCtx = useContext(chatContext);
  const [id, setId] = useState<string | undefined>(chatCtx?.chatId);

  useEffect(() => {
    setId(chatCtx?.chatId);
  }, [chatCtx?.chatId]);

  function hasExtraFeature(popup: PopupType) {
    if (!popup.requiredExtraFeature) return true;
    return userCtx?.hasExtraFeature(popup.requiredExtraFeature);
  }

  const filtered = popups
    .filter((p) => !p.hidden)
    .filter((p) => hasExtraFeature(p))
    .filter((p) => !userCtx?.isToken() || p.showToken)
    .filter((p) => !p.show || p.show(userCtx?.user, id));

  return (
    <List disablePadding className={className}>
      {filtered.map((p) => (
        <ListItem disablePadding key={p.title} sx={{ width: '100%' }}>
          {p.children?.length ? (
            <Submenu popup={p} drawerMenuOpen={drawerMenuOpen} opened={opened} setOpened={setOpened} />
          ) : (
            <MenuItem popup={p} drawerMenuOpen={drawerMenuOpen} />
          )}
        </ListItem>
      ))}
    </List>
  );
}

export function HomeLeftNav({ popups }: { popups: PopupType[] }) {
  const { i18n } = useTranslation();
  const [drawerMenuOpen, setDrawerMenuOpen] = useState<boolean>(false);
  const [opened, setOpened] = useState<string | undefined>(undefined);
  const href = languageSpecificHref();

  function languageSpecificHref() {
    switch (i18n.language) {
      case 'hu':
        return 'https://www.itrack.hu/';
      case 'sk':
        return 'https://www.itrack.sk/';
      case 'hr':
        return 'https://www.itrack.hr/';
      case 'cz':
        return 'https://www.itrackgps.cz/';
      default:
        return 'https://www.idatatelematics.com/';
    }
  }

  const mainPopups = popups.filter((p) => p.mainFunction);
  const secondaryPopups = popups.filter((p) => !p.mainFunction);

  function setOpenedWrapper(n: string | undefined) {
    setOpened(n);
  }

  return (
    <Box className={`home-nav ${drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}>
      <Drawer
        variant="permanent"
        className={`home-left-nav-navigator ${drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}
        classes={{ paper: drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed' }}
      >
        <div className="home-left-nav-list">
          <Button href={href} sx={{ width: '100%', p: 0, justifyContent: 'start' }} disableRipple target="_blank">
            <MenuHeader drawerMenuOpen={drawerMenuOpen} />
          </Button>
          <IconList popups={mainPopups} drawerMenuOpen={drawerMenuOpen} opened={opened} setOpened={setOpenedWrapper} />
          <div className="home-left-nav-secondary-list">
            <IconList popups={secondaryPopups} drawerMenuOpen={drawerMenuOpen} opened={opened} setOpened={setOpened} />
          </div>
        </div>
        <div className="home-left-nav-list-close-container">
          <Button
            onClick={() => setDrawerMenuOpen(!drawerMenuOpen)}
            className="home-left-nav-list-close"
            data-qa-element-id="home-left-nav-expand-collapse"
          >
            {drawerMenuOpen ? (
              <KeyboardDoubleArrowLeft fontSize="medium" />
            ) : (
              <KeyboardDoubleArrowRight fontSize="medium" />
            )}
          </Button>
        </div>
      </Drawer>
    </Box>
  );
}
